import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithProvider } from '../api/onboarding';
import { useOnboarding } from './OnboardingContext';

const featureSlides = [
  {
    title: 'Personalized Workouts',
    description: 'Tailored plans generated from your Apple Watch and Health data.',
  },
  {
    title: 'Progress Tracking & AI Insights',
    description: 'See your streaks, milestones, and smart recommendations instantly.',
  },
];

export function WelcomeStep() {
  const { markStepComplete, setCurrentStep } = useOnboarding();
  const [activeSlide, setActiveSlide] = useState(0);
  const [error, setError] = useState('');
  const [loadingProvider, setLoadingProvider] = useState(null);
  const navigate = useNavigate();

  const handleOAuth = async (provider) => {
    setError('');
    setLoadingProvider(provider);
    try {
      await signInWithProvider(provider);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoadingProvider(null);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-12">
      <div className="max-w-md w-full bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-slate-700/50">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-2xl font-bold mb-6">
            R
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Train smarter. Measure progress.</h1>
          <p className="text-slate-300 text-sm leading-relaxed">
            Unlock tailored workouts, track every session, and let AI coach your next breakthrough.
          </p>
        </div>

        <div className="relative bg-slate-700/50 rounded-2xl p-6 mb-8 border border-slate-600/30">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-cyan-400 mb-2">{featureSlides[activeSlide].title}</h2>
            <p className="text-sm text-slate-300 leading-relaxed">{featureSlides[activeSlide].description}</p>
          </div>
          <div className="flex justify-center gap-2 mt-4">
            {featureSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSlide(index)}
                className={[
                  'w-2 h-2 rounded-full transition-colors',
                  activeSlide === index ? 'bg-cyan-400' : 'bg-slate-500',
                ].join(' ')}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-700/50 text-red-200 rounded-xl px-4 py-3 mb-6 text-sm text-center">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            type="button"
            data-testid="onboarding-oauth-apple"
            onClick={() => handleOAuth('apple')}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg"
            disabled={loadingProvider !== null}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {loadingProvider === 'apple' ? 'Connecting…' : 'Continue with Apple'}
          </button>
          <button
            type="button"
            data-testid="onboarding-oauth-google"
            onClick={() => handleOAuth('google')}
            className="w-full flex items-center justify-center gap-3 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors"
            disabled={loadingProvider !== null}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#EA4335" d="M5.27 9.76A7.45 7.45 0 0 1 12 4.5c1.95 0 3.57.69 4.88 1.83l3.65-3.65C18.53.89 15.57 0 12 0 7.31 0 3.25 2.69 1.27 6.55l3.99 3.21z"/>
              <path fill="#34A853" d="M16.04 18.01A7.4 7.4 0 0 1 12 19.5a7.45 7.45 0 0 1-6.72-4.17l-4 3.09C3.25 21.31 7.3 24 12 24c3.47 0 6.45-.95 8.83-2.59l-3.82-3.15-.97-.25z"/>
              <path fill="#FBBC05" d="M21.8 10.09c.18.91.27 1.87.27 2.91 0 .83-.09 1.64-.25 2.42l-5.78-4.51.04-.18h5.72z"/>
              <path fill="#4285F4" d="M12 9.55v4.91h5.02c-.44 1.16-1.17 2.13-2.11 2.77l3.82 3.15c2.32-2.15 3.77-5.33 3.77-9.09 0-.87-.09-1.73-.25-2.55H12v.81z"/>
            </svg>
            {loadingProvider === 'google' ? 'Connecting…' : 'Continue with Google'}
          </button>
          <button
            type="button"
            data-testid="onboarding-email-button"
            onClick={() => {
              markStepComplete('welcome');
              setCurrentStep((prev) => prev + 1);
            }}
            className="w-full flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3.5 rounded-xl font-semibold transition-colors shadow-lg shadow-cyan-500/20"
          >
            Sign up with Email
          </button>
        </div>

        <div className="mt-8 text-center text-sm text-slate-400">
          Already have an account?{' '}
          <button
            type="button"
            className="text-cyan-400 hover:text-cyan-300 font-medium"
            onClick={() => navigate('/login')}
          >
            Log in
          </button>
        </div>
      </div>
    </div>
  );
}
